/**
 * MP4 / QuickTime (ISO-BMFF) metadata (§20).
 *
 * An MP4 is a tree of length-prefixed boxes. This reads the handful that
 * describe the media — how long it is, how big the picture is, and which codec
 * it needs — and touches no sample data at all: decoding and playback are the
 * platform's job (§20), and the point of reading the metadata here is to show
 * the file honestly and let the player answer "can I play this?" before the
 * controls appear.
 *
 * The walk is depth-bounded and never trusts a box size that runs past its
 * parent, because a malformed or hostile file can declare a box larger than the
 * file and a naive reader would loop or read out of bounds (§30).
 */
export interface Mp4Info {
  readonly durationSec: number;
  readonly width: number | null;
  readonly height: number | null;
  /** Sample-description type of the first video track: `avc1`, `hev1`, … */
  readonly videoCodec: string | null;
  /** Sample-description type of the first audio track: `mp4a`, … */
  readonly audioCodec: string | null;
  readonly hasVideo: boolean;
  readonly hasAudio: boolean;
  /** The `ftyp` major brand, e.g. `isom`, `mp42`, `qt  `. */
  readonly brand: string;
}

interface Box { readonly type: string; readonly start: number; readonly end: number; readonly headerLen: number }

const MAX_DEPTH = 8;

function readBoxes(view: DataView, from: number, to: number): Box[] {
  const boxes: Box[] = [];
  let pos = from;
  while (pos + 8 <= to) {
    let size = view.getUint32(pos, false);
    const type = fourcc(view, pos + 4);
    let headerLen = 8;
    if (size === 1) {
      // 64-bit largesize. The high word must be zero for any file we can hold
      // in memory; if it is not, the box is bigger than anything real.
      if (pos + 16 > to || view.getUint32(pos + 8, false) !== 0) break;
      size = view.getUint32(pos + 12, false);
      headerLen = 16;
    } else if (size === 0) {
      size = to - pos; // extends to the end of the parent
    }
    const end = pos + size;
    if (size < headerLen || end > to) break; // truncated or lying size (§30)
    boxes.push({ type, start: pos, end, headerLen });
    pos = end;
  }
  return boxes;
}

function fourcc(view: DataView, offset: number): string {
  return String.fromCharCode(
    view.getUint8(offset), view.getUint8(offset + 1),
    view.getUint8(offset + 2), view.getUint8(offset + 3),
  );
}

function find(view: DataView, boxes: readonly Box[], type: string): Box | undefined {
  return boxes.find((b) => b.type === type);
}

export function parseMp4(bytes: Uint8Array): Mp4Info | null {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const top = readBoxes(view, 0, bytes.length);
  const ftyp = find(view, top, 'ftyp');
  const moov = find(view, top, 'moov');
  // A valid MP4 must carry a movie box; without it there is nothing to read.
  if (!moov) return null;

  const brand = ftyp ? fourcc(view, ftyp.start + ftyp.headerLen) : '';

  let durationSec = 0;
  let width: number | null = null;
  let height: number | null = null;
  let videoCodec: string | null = null;
  let audioCodec: string | null = null;
  let hasVideo = false;
  let hasAudio = false;

  const moovBoxes = readBoxes(view, moov.start + moov.headerLen, moov.end);
  const mvhd = find(view, moovBoxes, 'mvhd');
  if (mvhd) {
    const v = view.getUint8(mvhd.start + mvhd.headerLen);
    const base = mvhd.start + mvhd.headerLen + 4;
    const [timescale, duration] = v === 1
      ? [view.getUint32(base + 16, false), Number(view.getBigUint64(base + 20, false))]
      : [view.getUint32(base + 8, false), view.getUint32(base + 12, false)];
    if (timescale > 0) durationSec = duration / timescale;
  }

  for (const trak of moovBoxes.filter((b) => b.type === 'trak')) {
    const trakBoxes = readBoxes(view, trak.start + trak.headerLen, trak.end);
    const mdia = find(view, trakBoxes, 'mdia');
    const handler = mdia ? handlerType(view, mdia) : null;
    const codec = mdia ? sampleCodec(view, mdia) : null;

    if (handler === 'vide') {
      hasVideo = true;
      if (!videoCodec) videoCodec = codec;
      const tkhd = find(view, trakBoxes, 'tkhd');
      if (tkhd && width === null) {
        // width/height are the last two 32-bit 16.16 fixed-point fields.
        width = Math.round(view.getUint32(tkhd.end - 8, false) / 65536) || null;
        height = Math.round(view.getUint32(tkhd.end - 4, false) / 65536) || null;
      }
    } else if (handler === 'soun') {
      hasAudio = true;
      if (!audioCodec) audioCodec = codec;
    }
  }

  return { durationSec, width, height, videoCodec, audioCodec, hasVideo, hasAudio, brand };
}

/** The `hdlr` handler type inside an `mdia` box: `vide`, `soun`, … */
function handlerType(view: DataView, mdia: Box): string | null {
  const boxes = readBoxes(view, mdia.start + mdia.headerLen, mdia.end);
  const hdlr = find(view, boxes, 'hdlr');
  if (!hdlr) return null;
  // version(1) flags(3) predefined(4) handler_type(4)
  return fourcc(view, hdlr.start + hdlr.headerLen + 8);
}

/** The first sample-description type inside `mdia → minf → stbl → stsd`. */
function sampleCodec(view: DataView, mdia: Box): string | null {
  const minf = descend(view, mdia, ['minf']);
  const stbl = minf ? descend(view, minf, ['stbl']) : null;
  const stsd = stbl ? descend(view, stbl, ['stsd']) : null;
  if (!stsd) return null;
  // stsd: version(1) flags(3) entryCount(4), then the first sample entry, whose
  // own header is [size:4][type:4].
  const entry = stsd.start + stsd.headerLen + 8;
  if (entry + 8 > stsd.end) return null;
  return fourcc(view, entry + 4);
}

function descend(view: DataView, parent: Box, path: readonly string[], depth = 0): Box | null {
  if (depth > MAX_DEPTH) return null;
  const boxes = readBoxes(view, parent.start + parent.headerLen, parent.end);
  const next = find(view, boxes, path[0]!);
  if (!next) return null;
  return path.length === 1 ? next : descend(view, next, path.slice(1), depth + 1);
}
